import React, { useState, useEffect, useRef } from "react";
import { httpsCallable } from "firebase/functions";
import { functions, auth } from "../../../config/firebaseConfig";
import Table from "../classlistspage/ClassListTable";
import useSortableData from "../../../hooks/useSortableData";
import useSelectableItems from "../../../hooks/useSelectableItems";
import "./GradebookPage.css";
import "../../../styles/common.css";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faPen,
  faHeadphones,
  faArrowUp,
} from "@fortawesome/free-solid-svg-icons";
import AudioGradingModal from "./AudioGradingModal";
import Pagination from "../Pagination";

const GRADEBOOK_CACHE_PREFIX = "gradebook_";
const GRADEBOOK_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const formatDate = (dateField) => {
  if (!dateField) return "N/A";
  const date = new Date(dateField);
  return date.toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatTotalScore = (score, maxScore) => {
  if (!score || !maxScore) return "-";
  const percentage = (score / maxScore) * 100;
  return `${Math.round(percentage)}% (${formatNumber(score)}/${formatNumber(
    maxScore
  )} pts)`;
};

const formatNumber = (num) => {
  return Number(num) % 1 === 0 ? Math.round(num) : Number(num).toFixed(2);
};

const isGameType = (questionType) => {
  return ["Flashcards", "The Guessinator", "Lucky Spin"].includes(questionType);
};

const GradebookPage = ({ setPageTitle }) => {
  const { courseId, moduleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [classLists, setClassLists] = useState([]);
  const [selectedClassList, setSelectedClassList] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleQuestions, setModuleQuestions] = useState([]);
  const [isLoadingCourseDetails, setIsLoadingCourseDetails] = useState(false);
  const [gradebookResults, setGradebookResults] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGradingModalOpen, setIsGradingModalOpen] = useState(false);
  const [currentGradingData, setCurrentGradingData] = useState(null);
  const [showResponses, setShowResponses] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const coursesPerPage = 15;
  const [userProfile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);
  const gridRef = useRef(null);

  const {
    items: sortedCourses,
    requestSort,
    sortConfig,
  } = useSortableData(courses);

  const {
    selectedItems: selectedCourses,
    handleSelectItem,
    handleBulkSelect,
  } = useSelectableItems();

  useEffect(() => {
    setPageTitle("Gradebook");
    fetchCourses();
  }, [setPageTitle]);

  useEffect(() => {
    if (courseId && courses.length > 0) {
      const course = courses.find((c) => c.id === courseId);
      if (course) {
        setSelectedCourse(course);
        handleRowClick(null, course, moduleId);
      }
    }
  }, [courseId, courses.length]);

  useEffect(() => {
    // Reset states when navigating to base gradebook route
    if (location.pathname === "/gradebook") {
      setSelectedCourse(null);
      setSelectedModule(null);
      setModuleQuestions([]);
      setClassLists([]);
      setSelectedClassList(null);
      setGradebookResults({});
    }
  }, [location.pathname]);

  const fetchCourses = async () => {
    try {
      const getCoursesFunction = httpsCallable(functions, "getCourses");
      const coursesResult = await getCoursesFunction();
      setCourses(coursesResult.data.courses || []);
    } catch (error) {
      console.error("Error fetching courses:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRowClick = async (e, course, targetModuleId = null) => {
    navigate(`/gradebook/courses/${course.id}`);
    setSelectedCourse(course);
    setIsLoadingCourseDetails(true);
    try {
      const getClassListsFunction = httpsCallable(functions, "getClassLists");
      const classListsResult = await getClassListsFunction();

      const courseClassLists = classListsResult.data.classLists.filter(
        (classList) =>
          classList.courseIds && classList.courseIds.includes(course.id)
      );

      const getClassListDataFunction = httpsCallable(
        functions,
        "getClassListData"
      );
      const classListDataResult = await getClassListDataFunction();
      const allStudents = classListDataResult.data.students;

      const populatedClassLists = courseClassLists.map((classList) => ({
        ...classList,
        students: Array.isArray(classList.students)
          ? classList.students.map((studentId) => {
              const studentData = allStudents.find((s) => s.id === studentId);
              return {
                id: studentId,
                name: studentData
                  ? `${studentData.firstName} ${studentData.lastName}`
                  : `Student ${studentId}`,
              };
            })
          : [],
      }));

      setClassLists(populatedClassLists);
      const firstClassList = populatedClassLists[0];
      if (firstClassList) {
        setSelectedClassList(firstClassList);
        await fetchGradebookResults(course, firstClassList);

        if (targetModuleId) {
          const module = course.sections
            .flatMap((section) => section.modules)
            .find((m) => m.id === targetModuleId);
          if (module) {
            handleModuleClick(module, false);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching class lists:", error);
    } finally {
      setIsLoadingCourseDetails(false);
    }
  };

  const handleModuleClick = async (module, shouldNavigate = true) => {
    if (!selectedCourse) return;
    if (shouldNavigate) {
      navigate(`/gradebook/courses/${selectedCourse.id}/modules/${module.id}`);
    }
    setSelectedModule(module);
    const questions =
      module.materials?.filter(
        (material) =>
          (material.type === "questions" && material.questionId) ||
          material.type === "audio-recorder"
      ) || [];
    setModuleQuestions(questions);
  };

  const fetchGradebookResults = async (course, classList) => {
    try {
      // Check frontend cache first
      const cached = getCachedGradebook(course.id, classList.id);
      if (cached) {
        console.log("Using frontend cached gradebook data");
        setGradebookResults(cached);
        return;
      }

      const getGradebookResultsFunction = httpsCallable(
        functions,
        "getGradebookResults"
      );
      const response = await getGradebookResultsFunction({
        courseId: course.id,
        classListId: classList.id,
      });

      // Cache the results
      setCachedGradebook(course.id, classList.id, response.data.results);
      setGradebookResults(response.data.results);
      console.log(
        `Gradebook data ${
          response.data.fromCache ? "from backend cache" : "freshly fetched"
        }`
      );
    } catch (error) {
      console.error("Error fetching gradebook results:", error);
    }
  };

  const filteredCourses = sortedCourses.filter((course) =>
    course.courseName.toLowerCase().includes(filterText.toLowerCase())
  );

  const indexOfLastCourse = currentPage * coursesPerPage;
  const indexOfFirstCourse = indexOfLastCourse - coursesPerPage;
  const currentCourses = filteredCourses.slice(
    indexOfFirstCourse,
    indexOfLastCourse
  );
  const totalPages = Math.ceil(filteredCourses.length / coursesPerPage);

  const handlePlayPause = (audioUrl) => {
    if (currentAudio?.src === audioUrl) {
      if (isPlaying) {
        currentAudio.pause();
      } else {
        currentAudio.play();
      }
    } else {
      if (currentAudio) {
        currentAudio.pause();
      }
      const audio = new Audio(audioUrl);
      audio.addEventListener("ended", () => {
        setIsPlaying(false);
      });
      audio.play();
      setCurrentAudio(audio);
    }
    setIsPlaying(!isPlaying);
  };

  const renderQuestionCell = (question, questionScore, student) => {
    const formatScore = (score, maxScore) => {
      if (isGameType(question.questionType)) return "Game";
      if (score === undefined || score === null) return `0/${maxScore}`;
      return `${formatNumber(score)}/${formatNumber(maxScore)}`;
    };

    const cellStyle = {
      display: "flex",
      flexDirection: "column",
      gap: "4px",
    };

    const answerStyle = {
      fontSize: "0.85em",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    };

    if (question.type === "audio-recorder") {
      return (
        <div className="gradebook-cell grade-cell audio-cell" style={cellStyle}>
          <div className="score-line">
            {questionScore?.grade
              ? formatScore(
                  questionScore.grade.score,
                  question.score || 0,
                  true
                )
              : formatScore(0, question.score || 0, true)}
          </div>
          <div className="answer-line" style={answerStyle}>
            {questionScore?.audioUrl ? (
              <div className="audio-controls">
                <button
                  className="gradebook-icon-button play-pause-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayPause(questionScore.audioUrl);
                  }}
                >
                  <FontAwesomeIcon
                    icon={
                      currentAudio?.src === questionScore.audioUrl && isPlaying
                        ? faPause
                        : faPlay
                    }
                    size="sm"
                  />
                </button>
                <button
                  className="gradebook-icon-button grade-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleGradeAudio(student, question, questionScore);
                  }}
                >
                  <FontAwesomeIcon icon={faPen} size="xs" />
                </button>
              </div>
            ) : (
              <span className="gradebook-status-text no-answer">
                No Recording <FontAwesomeIcon icon={faHeadphones} size="sm" />
              </span>
            )}
          </div>
        </div>
      );
    }

    // For non-audio questions
    return (
      <div className="gradebook-cell grade-cell" style={cellStyle}>
        <div className="gradebook-score-line">
          {formatScore(questionScore?.score, question.score || 0, false)}
        </div>
        <div className="gradebook-answer-line" style={answerStyle}>
          {questionScore?.hasResponse ? (
            <>
              {isGameType(question.questionType) ? (
                <span className="gradebook-status-text correct">Complete</span>
              ) : (
                <>
                  <span
                    className={`gradebook-status-text ${
                      questionScore.score > 0 ? "correct" : "incorrect"
                    }`}
                  >
                    {questionScore.score > 0 ? "Correct" : "Incorrect"}
                  </span>
                  {showResponses && questionScore.response && (
                    <div className="gradebook-response-text">
                      {Array.isArray(questionScore.response)
                        ? question.questionType === "Sentence Ordering"
                          ? questionScore.response.join(" ")
                          : question.questionType === "Multiple Checkbox"
                          ? questionScore.response
                              .map((index) => question.options[index])
                              .join(", ")
                          : question.questionType === "Pairing"
                          ? questionScore.response
                              .map((pair) => `${pair.left} → ${pair.right}`)
                              .join(", ")
                          : questionScore.response.join(", ")
                        : question.questionType === "Multiple Choice"
                        ? question.options[questionScore.response]
                        : String(questionScore.response)}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            <span className="gradebook-status-text no-answer">No Response</span>
          )}
        </div>
      </div>
    );
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - gridRef.current.offsetLeft);
    setStartY(e.pageY - gridRef.current.offsetTop);
    setScrollLeft(gridRef.current.scrollLeft);
    setScrollTop(gridRef.current.scrollTop);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - gridRef.current.offsetLeft;
    const y = e.pageY - gridRef.current.offsetTop;
    const walkX = (x - startX) * 1.5;
    const walkY = (y - startY) * 1.5;
    gridRef.current.scrollLeft = scrollLeft - walkX;
    gridRef.current.scrollTop = scrollTop - walkY;
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const renderGradebook = (course) => {
    // Only show "No students found" if we have finished loading and actually have no students
    if (
      !isLoadingCourseDetails &&
      selectedClassList &&
      (!selectedClassList.students || selectedClassList.students.length === 0)
    ) {
      return (
        <div className="loading">
          No students found in the selected class list
        </div>
      );
    }

    return (
      <div className="gradebook-course">
        <div className="status-bar">
          {selectedCourse && (
            <div className="status-bar">
              <span
                className="status-field clickable-course-name"
                onClick={handleCourseNameClick}
              >
                Course: {selectedCourse.courseName}
              </span>
              <span className="status-field">Owner: {course.ownerName}</span>
              <span className="status-field">
                Created: {formatDate(course.creationDate)}
              </span>
            </div>
          )}
          <div className="button-group">
            {selectedModule && (
              <button
                className="show-responses-button"
                onClick={() => setShowResponses(!showResponses)}
              >
                {showResponses ? "Hide Responses" : "Show Responses"}
              </button>
            )}
            <button
              className="refresh-button"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Refreshing..." : "Refresh Grades"}
            </button>
          </div>
        </div>

        {classLists.length > 1 && (
          <div className="class-list-selector">
            <label>Select Class List: </label>
            <select
              value={selectedClassList?.id || ""}
              onChange={(e) => {
                const newClassList = classLists.find(
                  (cl) => cl.id === e.target.value
                );
                setSelectedClassList(newClassList);
                if (newClassList && selectedCourse) {
                  fetchGradebookResults(selectedCourse, newClassList);
                }
              }}
            >
              {classLists.map((classList) => (
                <option key={classList.id} value={classList.id}>
                  {classList.className}
                </option>
              ))}
            </select>
          </div>
        )}

        <div
          className="gradebook-grid"
          ref={gridRef}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
          style={{
            cursor: isDragging ? "grabbing" : "grab",
            userSelect: "none",
            overflow: "auto",
            maxHeight: "75vh",
          }}
        >
          <div className="gradebook-headers">
            <div className="gradebook-row header-row">
              <div className="gradebook-cell corner-cell">Students</div>
              {selectedModule
                ? (() => {
                    let questionCounter = 0;
                    let gameCounter = 0;
                    return moduleQuestions.map((question) => {
                      if (isGameType(question.questionType)) {
                        gameCounter++;
                        return (
                          <div
                            key={question.questionId}
                            className="gradebook-cell module-header"
                          >
                            {`Game ${gameCounter}`}
                          </div>
                        );
                      } else {
                        questionCounter++;
                        return (
                          <div
                            key={question.questionId}
                            className="gradebook-cell module-header"
                          >
                            <div>{`Question ${questionCounter}`}</div>
                            {showResponses && (
                              <div
                                style={{
                                  fontSize: "0.8em",
                                  display: "-webkit-box",
                                  WebkitLineClamp: "2",
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  opacity: 0.7,
                                  marginTop: "2px",
                                  lineHeight: "1.2",
                                }}
                              >
                                {question.question ||
                                  question.content ||
                                  question.sentence ||
                                  ""}
                              </div>
                            )}
                          </div>
                        );
                      }
                    });
                  })()
                : course.sections?.map((section) => [
                    <div
                      key={`section-${section.id}`}
                      className="gradebook-cell section-header"
                    >
                      {section.name}
                    </div>,
                    ...(section.modules?.map((module) => (
                      <div
                        key={`module-${module.id}`}
                        className="gradebook-cell module-header"
                        onClick={() => handleModuleClick(module)}
                        style={{ cursor: "pointer" }}
                      >
                        <div className="module-header-content">
                          <span>{module.name}</span>
                          <FontAwesomeIcon
                            icon={faArrowUp}
                            size="sm"
                            style={{
                              opacity: 0.7,
                              marginLeft: "8px",
                              transform: "rotate(45deg)",
                            }}
                            title="Click to view module details"
                          />
                        </div>
                      </div>
                    )) || []),
                  ])}
              <div className="gradebook-cell total-header">
                {selectedModule ? "Module Total" : "Course Total"}
              </div>
            </div>
          </div>

          <div className="gradebook-body">
            {isLoadingCourseDetails || !selectedClassList ? (
              <div className="gradebook-row">
                <div className="gradebook-cell loading-cell">
                  Loading grades...
                </div>
              </div>
            ) : (
              selectedClassList.students.map((student) => (
                <div key={student.id} className="gradebook-row">
                  <div
                    className="gradebook-cell student-name"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <span>{student.name || `Student ${student.id}`}</span>
                    {!selectedModule && (
                      <button
                        className="view-profile-button"
                        onClick={() => {
                          navigate(`/gradebook/students/${student.id}`, {
                            state: {
                              returnPath: `/gradebook/courses/${selectedCourse.id}`,
                              courseName: selectedCourse.courseName,
                            },
                          });
                        }}
                        title="View Student Profile"
                      >
                        <span
                          style={{ marginRight: "4px", fontSize: "0.85em" }}
                        >
                          View Profile
                        </span>
                        <FontAwesomeIcon icon={faArrowUp} size="sm" />
                      </button>
                    )}
                    {selectedModule && (
                      <button
                        className="view-submission-button"
                        onClick={() => {
                          const section = selectedCourse.sections.find(
                            (section) =>
                              section.modules.some(
                                (m) => m.id === selectedModule.id
                              )
                          );
                          if (section) {
                            const url = `/sview/courses/${selectedCourse.id}/sections/${section.id}/modules/${selectedModule.id}/student/${student.id}`;
                            window.open(url, "_blank");
                          }
                        }}
                        title="View Student Submission"
                      >
                        <span
                          style={{ marginRight: "4px", fontSize: "0.85em" }}
                        >
                          View Submission
                        </span>
                        <FontAwesomeIcon icon={faArrowUp} size="sm" />
                      </button>
                    )}
                  </div>
                  {selectedModule
                    ? moduleQuestions.map((question) => {
                        const moduleResult = gradebookResults?.[
                          student.id
                        ]?.find((section) =>
                          section?.moduleScores?.some(
                            (m) => m?.moduleId === selectedModule.id
                          )
                        );
                        const moduleScore = moduleResult?.moduleScores?.find(
                          (m) => m?.moduleId === selectedModule.id
                        );
                        const questionScore =
                          moduleScore?.questionScores?.[question.questionId];

                        return (
                          <React.Fragment key={`grade-${question.questionId}`}>
                            {renderQuestionCell(
                              question,
                              questionScore,
                              student
                            )}
                          </React.Fragment>
                        );
                      })
                    : course.sections?.map((section) => {
                        const sectionScore = gradebookResults?.[
                          student.id
                        ]?.find((s) => s?.sectionId === section.id);
                        return [
                          <div
                            key={`grade-section-${section.id}`}
                            className="gradebook-cell grade-cell section-grade"
                          >
                            {sectionScore
                              ? formatTotalScore(
                                  sectionScore.totalScore,
                                  sectionScore.maxScore
                                )
                              : "-"}
                          </div>,
                          ...(section.modules?.map((module) => {
                            const moduleScore =
                              sectionScore?.moduleScores?.find(
                                (m) => m?.moduleId === module.id
                              );
                            return (
                              <div
                                key={`grade-module-${module.id}`}
                                className="gradebook-cell grade-cell"
                              >
                                {moduleScore
                                  ? formatTotalScore(
                                      moduleScore.score,
                                      moduleScore.maxScore
                                    )
                                  : "-"}
                              </div>
                            );
                          }) || []),
                        ];
                      })}
                  <div className="gradebook-cell grade-cell total-column">
                    {selectedModule
                      ? (() => {
                          const moduleResult = gradebookResults?.[
                            student.id
                          ]?.find((section) =>
                            section?.moduleScores?.some(
                              (m) => m?.moduleId === selectedModule.id
                            )
                          );
                          const moduleScore = moduleResult?.moduleScores?.find(
                            (m) => m?.moduleId === selectedModule.id
                          );
                          return moduleScore
                            ? formatTotalScore(
                                moduleScore.score,
                                moduleScore.maxScore
                              )
                            : "-";
                        })()
                      : (() => {
                          const studentResults = gradebookResults?.[student.id];
                          if (!studentResults) return "-";
                          const totalScore = studentResults.reduce(
                            (sum, section) => sum + section.totalScore,
                            0
                          );
                          const maxScore = studentResults.reduce(
                            (sum, section) => sum + section.maxScore,
                            0
                          );
                          return maxScore > 0
                            ? formatTotalScore(totalScore, maxScore)
                            : "-";
                        })()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleBackToCourseSelection = () => {
    navigate("/gradebook", { replace: true });
    setSelectedCourse(null);
    setSelectedModule(null);
    setModuleQuestions([]);
    setClassLists([]);
    setSelectedClassList(null);
    setGradebookResults({});
    fetchCourses();
  };

  const handleRefresh = async () => {
    if (!selectedCourse || !selectedClassList) return;
    setIsRefreshing(true);
    try {
      // Clear frontend cache
      const key = `${GRADEBOOK_CACHE_PREFIX}${selectedCourse.id}_${selectedClassList.id}`;
      localStorage.removeItem(key);

      const getGradebookResultsFunction = httpsCallable(
        functions,
        "getGradebookResults"
      );
      const response = await getGradebookResultsFunction({
        courseId: selectedCourse.id,
        classListId: selectedClassList.id,
        forceRefresh: true,
      });
      setGradebookResults(response.data.results);
      // Update frontend cache with fresh data
      setCachedGradebook(
        selectedCourse.id,
        selectedClassList.id,
        response.data.results
      );
      console.log("Gradebook data freshly fetched");
    } catch (error) {
      console.error("Error refreshing gradebook results:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleGradeAudio = (student, question, questionScore) => {
    const moduleQuestion = moduleQuestions.find(
      (q) => q.questionId === question.questionId
    );

    const studentResponse =
      gradebookResults[student.id]?.responses?.[question.questionId];
    console.log("Student Response:", studentResponse);

    setCurrentGradingData({
      student,
      question: {
        ...question,
        rubric: moduleQuestion?.rubric || null,
      },
      questionScore,
      audioUrl: studentResponse?.answer,
    });
    setIsGradingModalOpen(true);
  };

  const handleCourseNameClick = () => {
    if (selectedCourse) {
      navigate(`/courses/${selectedCourse.id}`);
    }
  };

  const handleGradeSubmit = async (grade) => {
    try {
      const submitGrade = httpsCallable(functions, "submitAudioGrade");
      await submitGrade({
        studentId: currentGradingData.student.id,
        questionId: currentGradingData.question.questionId,
        courseId: selectedCourse.id,
        moduleId: selectedModule.id,
        grade: {
          score: grade.score,
          feedback: grade.feedback,
          selectedCells: grade.selectedCells,
          graderName: auth.currentUser?.displayName || "Unknown Teacher",
          graderAvatar: userProfile?.avatar || "Avatar0",
        },
      });

      await refreshGrades();
      setIsGradingModalOpen(false);
    } catch (error) {
      console.error("Error submitting grade:", error);
      alert("Failed to submit grade. Please try again.");
    }
  };

  const refreshGrades = async () => {
    if (!selectedCourse || !selectedClassList) return;

    setIsRefreshing(true);
    try {
      const getGradebookResults = httpsCallable(
        functions,
        "getGradebookResults"
      );
      const result = await getGradebookResults({
        courseId: selectedCourse.id,
        classListId: selectedClassList.id,
        forceRefresh: true,
      });

      setGradebookResults(result.data.results || {});
    } catch (error) {
      console.error("Error refreshing grades:", error);
      alert("Failed to refresh grades. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  const getCachedGradebook = (courseId, classListId) => {
    const key = `${GRADEBOOK_CACHE_PREFIX}${courseId}_${classListId}`;
    const cached = localStorage.getItem(key);

    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const timeLeft = GRADEBOOK_CACHE_DURATION - (Date.now() - timestamp);
      console.log(`Gradebook cache check:`, {
        found: true,
        timeLeftMinutes: Math.round(timeLeft / 1000 / 60),
      });

      if (timeLeft > 0) {
        return data;
      }
      localStorage.removeItem(key);
    }
    return null;
  };

  const setCachedGradebook = (courseId, classListId, data) => {
    const key = `${GRADEBOOK_CACHE_PREFIX}${courseId}_${classListId}`;
    const cache = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(cache));
    console.log(`Gradebook cache set for ${key}`);
  };

  return (
    <div className="gradebook-page">
      <div className="main-content">
        {selectedCourse ? (
          <>
            <button
              className="gradebook-back-button"
              onClick={
                selectedModule
                  ? () => {
                      navigate(`/gradebook/courses/${selectedCourse.id}`);
                      setSelectedModule(null);
                      setModuleQuestions([]);
                    }
                  : handleBackToCourseSelection
              }
            >
              {selectedModule
                ? "← Back to Course Overview"
                : "← Back to Course Selection"}
            </button>
            {renderGradebook(selectedCourse)}
          </>
        ) : (
          <div className="courses-list">
            <div className="top-controls">
              <button
                className="create-course-button"
                onClick={() => alert("Grade a Student feature coming soon!")}
              >
                Grade a Student
              </button>
              <div className="filter-container">
                <input
                  type="text"
                  placeholder="Filter courses..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                  className="filter-input"
                />
              </div>
            </div>
            <Table
              data={isLoading ? [] : currentCourses}
              columns={[
                { key: "courseName", label: "Course Name" },
                { key: "ownerName", label: "Owner" },
                { key: "creationDate", label: "Created" },
              ]}
              selectedItems={selectedCourses}
              handleSelectItem={handleSelectItem}
              handleBulkSelect={handleBulkSelect}
              requestSort={requestSort}
              getSortArrow={(key) =>
                sortConfig.key === key ? (
                  sortConfig.direction === "ascending" ? (
                    <span className="arrow up"></span>
                  ) : (
                    <span className="arrow down"></span>
                  )
                ) : null
              }
              onRowClick={handleRowClick}
              isLoading={isLoading}
              formatters={{
                creationDate: formatDate,
                lastEditDate: formatDate,
              }}
            />

            <div className="spacer" style={{ height: "20px" }}></div>

            <Pagination
              totalPages={totalPages}
              currentPage={currentPage}
              handlePageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
      {isGradingModalOpen && (
        <AudioGradingModal
          isOpen={isGradingModalOpen}
          onClose={() => setIsGradingModalOpen(false)}
          onSubmit={handleGradeSubmit}
          question={currentGradingData?.question}
          currentGrade={currentGradingData?.questionScore}
          audioUrl={currentGradingData?.questionScore?.response}
          courseId={selectedCourse?.id}
        />
      )}
    </div>
  );
};

export default GradebookPage;
